
import { useState } from 'react';
import { PricingPlan } from './PricingCard';

/**
 * Custom hook that manages the pricing plans data and calculates 
 * prices based on billing cycle (monthly/annual)
 */
export const usePricingPlans = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  
  // Calculate annual prices with 15% discount
  const getAnnualPrice = (monthlyPrice: number) => {
    return Number((monthlyPrice * 0.85).toFixed(2));
  };

  /**
   * Calculates the price based on billing cycle
   * @param monthlyPrice The monthly price
   * @returns The calculated price depending on annual/monthly choice
   */
  const calculatePrice = (monthlyPrice: number) => {
    if (!isAnnual) return monthlyPrice;
    return getAnnualPrice(monthlyPrice);
  };

  /**
   * Calculates annual savings
   * @param monthlyPrice The monthly price
   * @returns The amount saved when choosing annual billing
   */
  const getAnnualSavings = (monthlyPrice: number) => {
    if (!isAnnual) return 0;
    const annualSavings = monthlyPrice * 12 * 0.15; // 15% savings
    return Number(annualSavings.toFixed(2));
  };

  /**
   * Calculates the annual total price
   * @param monthlyPrice The monthly price
   * @returns The total annual price with discount
   */
  const getAnnualTotal = (monthlyPrice: number) => {
    if (!isAnnual) return 0;
    return Number((monthlyPrice * 12 * 0.85).toFixed(2)); // 15% discount on annual
  };
  
  /**
   * Calculates price per report
   * @param price The price
   * @param reportCount The number of reports
   * @returns Price per report formatted as string
   */
  const getPricePerReport = (price: number, reportCount: number) => {
    if (reportCount === 0) return "Free";
    const pricePerReport = price / reportCount;
    return `$${pricePerReport.toFixed(2)}/report`;
  };
  
  const plans: PricingPlan[] = [
    {
      name: "Starter",
      price: calculatePrice(7.99),
      period: "per month",
      description: "Perfect for individuals and small teams",
      features: [
        "10 reports per month", 
        "PDF export",
        "30-day report history",
        getPricePerReport(calculatePrice(7.99), 10)
      ],
      cta: "Get Started",
      ctaLink: "/auth",
      popular: false,
      discount: isAnnual ? "15% off" : null,
      annualTotal: isAnnual ? getAnnualTotal(7.99) : null,
      priceId: {
        monthly: "price_1REDpqApS1Q0yn5q0JC6ZeRd",
        annual: "price_1REDvIApS1Q0yn5qeAZ25dj2"
      }
    },
    {
      name: "Pro",
      price: calculatePrice(17.99),
      period: "per month",
      description: "Ideal for growing businesses",
      features: [
        "30 reports per month",
        "PDF export",
        "Unlimited report history",
        "Priority support",
        getPricePerReport(calculatePrice(17.99), 30)
      ],
      cta: "Get Started",
      ctaLink: "/auth",
      popular: true,
      discount: isAnnual ? "15% off" : null,
      annualTotal: isAnnual ? getAnnualTotal(17.99) : null,
      priceId: {
        monthly: "price_1REDtEApS1Q0yn5q9WEvuTOr",
        annual: "price_1REDvnApS1Q0yn5qciFL7emK"
      }
    },
    {
      name: "Premium",
      price: calculatePrice(29.99),
      period: "per month",
      description: "Full power for large organizations",
      features: [
        "60 reports per month",
        "PDF export",
        "Unlimited report history",
        "Priority support",
        getPricePerReport(calculatePrice(29.99), 60)
      ],
      cta: "Get Started",
      ctaLink: "/auth",
      popular: false,
      discount: isAnnual ? "15% off" : null,
      annualTotal: isAnnual ? getAnnualTotal(29.99) : null,
      priceId: {
        monthly: "price_1REDuaApS1Q0yn5qCu5MH3ev",
        annual: "price_1REDwzApS1Q0yn5qpaReOIrg"
      }
    }
  ];

  return {
    plans,
    isAnnual,
    setIsAnnual,
    calculatePrice,
    getAnnualSavings,
    getAnnualTotal,
    getPricePerReport
  };
};
